import { useEffect, useMemo, useState } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { getEmailNewsUsers, sendEmailNews } from '../../services/EmailNewsService';

function EmailNews() {
    const [mode, setMode] = useState('all');
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [sending, setSending] = useState(false);
    const [notification, setNotification] = useState({ type: '', text: '' });

    const canSend = useMemo(() => {
        if (!subject.trim() || !message.trim()) return false;
        if (mode === 'one' && !selectedUserId) return false;
        return true;
    }, [message, mode, selectedUserId, subject]);

    useEffect(() => {
        setLoadingUsers(true);
        getEmailNewsUsers()
            .then(result => {
                setUsers(result || []);
                if ((result || []).length === 1) {
                    setSelectedUserId(String(result[0].id));
                }
            })
            .catch(err => {
                const errorMsg = err.response ? err.response.data : err.message;
                setNotification({ type: 'error', text: errorMsg || 'Erro ao carregar usuários' });
            })
            .finally(() => setLoadingUsers(false));
    }, []);

    function onSubmit(e) {
        e.preventDefault();
        setNotification({ type: '', text: '' });

        if (!canSend) return;

        const sendToAll = mode === 'all';
        setSending(true);
        sendEmailNews({
            sendToAll,
            userId: sendToAll ? undefined : selectedUserId,
            subject: subject.trim(),
            message: message.trim()
        })
            .then(result => {
                if (result && result.failed && result.failed > 0) {
                    setNotification({
                        type: 'warning',
                        text: `Enviados: ${result.sent}. Falhas: ${result.failed}.`
                    });
                } else {
                    setNotification({
                        type: 'success',
                        text: `E-mails enviados com sucesso! (${result.sent || 1})`
                    });
                }
            })
            .catch(err => {
                let errorMsg = err.response ? err.response.data : err.message;
                if (typeof errorMsg === 'object') {
                    errorMsg = errorMsg.message || JSON.stringify(errorMsg);
                }
                setNotification({ type: 'error', text: errorMsg || 'Erro ao enviar e-mails' });
            })
            .finally(() => setSending(false));
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">E-mail em massa</h1>
                        <p className="mb-0 text-muted">
                            Envie um e-mail para todos os usuários ativos com e-mail cadastrado, ou para apenas um usuário.
                            O remetente e a API MailerSend vêm das Configurações do sistema.
                        </p>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="card card-body border-0 shadow mb-4">
                            <form onSubmit={onSubmit}>
                                <div className="row">
                                    <div className="col-12 mb-3">
                                        <label className="form-label d-block">Destino</label>
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="modeEmail"
                                                id="modeAllEmail"
                                                value="all"
                                                checked={mode === 'all'}
                                                onChange={() => setMode('all')}
                                            />
                                            <label className="form-check-label" htmlFor="modeAllEmail">
                                                Todos os usuários ativos
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="modeEmail"
                                                id="modeOneEmail"
                                                value="one"
                                                checked={mode === 'one'}
                                                onChange={() => setMode('one')}
                                            />
                                            <label className="form-check-label" htmlFor="modeOneEmail">
                                                Apenas um usuário
                                            </label>
                                        </div>
                                        <div className="mt-2 text-muted">
                                            {loadingUsers
                                                ? 'Carregando lista...'
                                                : `Usuários ativos com e-mail: ${users.length}`}
                                        </div>
                                    </div>
                                </div>

                                {mode === 'one' && (
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="userIdEmail" className="form-label">Usuário</label>
                                            <select
                                                id="userIdEmail"
                                                className="form-select"
                                                value={selectedUserId}
                                                onChange={e => setSelectedUserId(e.target.value)}
                                                disabled={loadingUsers || users.length === 0}
                                            >
                                                <option value="">
                                                    {users.length ? 'Selecione um usuário' : 'Nenhum usuário disponível'}
                                                </option>
                                                {users.map(u => (
                                                    <option key={u.id} value={String(u.id)}>
                                                        {u.name ? `${u.name} — ${u.email}` : u.email}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                <div className="row">
                                    <div className="col-12 mb-3">
                                        <label htmlFor="subjectEmail" className="form-label">Assunto</label>
                                        <input
                                            id="subjectEmail"
                                            type="text"
                                            className="form-control"
                                            placeholder="Assunto do e-mail"
                                            value={subject}
                                            onChange={e => setSubject(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-12 mb-3">
                                        <label htmlFor="messageEmail" className="form-label">Mensagem</label>
                                        <textarea
                                            id="messageEmail"
                                            className="form-control"
                                            rows="8"
                                            placeholder="Corpo do e-mail (texto; quebras de linha viram parágrafos no HTML)"
                                            value={message}
                                            onChange={e => setMessage(e.target.value)}
                                        />
                                        <small className="text-muted d-block mt-2">
                                            Envios em massa têm um pequeno intervalo entre cada e-mail para respeitar limites da API.
                                        </small>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-sm-3">
                                        <button
                                            type="submit"
                                            className="btn btn-gray-800 mt-2 animate-up-2"
                                            disabled={!canSend || sending}
                                        >
                                            {sending ? 'Enviando...' : 'Enviar e-mails'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default EmailNews;
